import React from 'react'
import { Heart, Target, Eye, Award, Users, Calendar, MapPin, GraduationCap } from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'

const About = () => {
  const timeline = [
    {
      year: "2017",
      title: "Fundación",
      description: "Inicio de actividades educativas con visión de excelencia académica"
    },
    {
      year: "2018-2019",
      title: "Crecimiento",
      description: "Expansión de programas y fortalecimiento del equipo docente"
    },
    {
      year: "2020-2021",
      title: "Adaptación",
      description: "Implementación exitosa de educación virtual durante la pandemia"
    },
    {
      year: "2022-2024",
      title: "Consolidación",
      description: "Reconocimiento como institución destacada en la región"
    },
    {
      year: "2025",
      title: "Presente",
      description: "2,200 estudiantes y 70 educadores comprometidos con la excelencia"
    }
  ]

  const values = [
    {
      icon: Heart,
      title: "Respeto",
      description: "Valoramos la dignidad de cada persona y promovemos la convivencia pacífica en toda nuestra comunidad educativa."
    },
    {
      icon: Target,
      title: "Responsabilidad", 
      description: "Fomentamos el compromiso con el aprendizaje, el desarrollo personal y el cumplimiento de deberes."
    },
    {
      icon: Award,
      title: "Excelencia",
      description: "Buscamos constantemente la mejora continua en todos nuestros procesos educativos y formativos."
    },
    {
      icon: Users,
      title: "Integridad",
      description: "Actuamos con honestidad, coherencia y transparencia en todas nuestras acciones y decisiones."
    }
  ]

  const team = [
    {
      role: "Directivos",
      count: "8",
      description: "Liderando con visión estratégica"
    },
    {
      role: "Docentes",
      count: "55",
      description: "Profesionales comprometidos"
    },
    {
      role: "Administrativos",
      count: "7",
      description: "Apoyo integral constante"
    }
  ]

  return (
    
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gardenia-red/10 to-gardenia-green/10">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green shadow-2xl mb-6">
                <GraduationCap className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gardenia-dark mb-6">
              Conoce Nuestra 
              <span className="text-gradient block mt-2">Historia</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">              
              Desde 2017, la Institución Educativa Distrital Las Gardenias ha sido un referente 
              en educación de calidad en Barranquilla, formando ciudadanos íntegros bajo el lema 
              "Paz, Educación y Progreso".
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/programas" size="lg">
                Conoce Nuestros Programas
              </Button>
              <Button to="/contacto" variant="outline" size="lg">
                Contáctanos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Objectives */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Nuestra Identidad Institucional
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Los pilares fundamentales que guían nuestro quehacer educativo y definen 
              el rumbo de nuestra institución hacia la excelencia académica.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Misión */}
            <Card className="text-center" variant="bordered" padding="lg">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-red to-red-600 mb-4">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <Card.Title className="text-2xl text-gardenia-red">Misión</Card.Title>
              </div>
              <Card.Content>
                Formar ciudadanos íntegros con competencias académicas, sociales y emocionales 
                sólidas, que contribuyan al desarrollo sostenible de la sociedad, promoviendo 
                valores de paz, respeto y responsabilidad.
              </Card.Content>
            </Card>

            {/* Visión */}
            <Card className="text-center" variant="bordered" padding="lg">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-green to-green-600 mb-4">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <Card.Title className="text-2xl text-gardenia-green">Visión</Card.Title>
              </div>
              <Card.Content>
                Ser reconocidos como una institución educativa líder en formación integral 
                y excelencia académica en la región Caribe, caracterizada por la innovación 
                pedagógica y el compromiso social.
              </Card.Content>
            </Card>

            {/* Objetivos */}
            <Card className="text-center" variant="bordered" padding="lg">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-gold to-yellow-500 mb-4">
                  <Target className="h-8 w-8 text-gardenia-dark" />
                </div>
                <Card.Title className="text-2xl text-gardenia-red">Objetivos</Card.Title>
              </div>
              <Card.Content>
                Promover el desarrollo integral de nuestros estudiantes a través de metodologías 
                innovadoras, valores sólidos y un ambiente educativo que fomente el pensamiento 
                crítico y la creatividad.
              </Card.Content>
            </Card>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Nuestra Trayectoria
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un recorrido por los momentos más importantes de nuestra historia institucional.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-gardenia-red to-gardenia-green hidden lg:block"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } flex-col lg:space-x-8`}>
                  {/* Content */}
                  <div className="lg:w-1/2 mb-8 lg:mb-0">
                    <Card className={`${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} text-center`}>
                      <div className="mb-4">
                        <span className="inline-block px-4 py-2 bg-gardenia-red text-white rounded-full font-bold text-lg mb-3">
                          {item.year}
                        </span>
                        <Card.Title className="text-xl">{item.title}</Card.Title>
                      </div>
                      <Card.Content>
                        {item.description}
                      </Card.Content>
                    </Card>
                  </div>

                  {/* Timeline Node */}
                  <div className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full bg-gardenia-gold border-4 border-white shadow-lg z-10">
                    <div className="w-3 h-3 bg-gardenia-red rounded-full"></div>
                  </div>

                  {/* Spacer */}
                  <div className="lg:w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gardenia-dark text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Nuestros Valores
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Los principios fundamentales que orientan nuestras acciones y definen 
              nuestra cultura institucional.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon
              return (
                <div key={index} className="text-center">
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gardenia-red/20 border-2 border-gardenia-red mb-4">
                      <IconComponent className="h-8 w-8 text-gardenia-red" />
                    </div>
                    <h3 className="text-xl font-bold text-gardenia-gold mb-3">
                      {value.title}
                    </h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Nuestro Equipo Humano
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              70 profesionales comprometidos con la educación de calidad y la formación 
              integral de nuestros 2,200 estudiantes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {team.map((member, index) => (
              <Card key={index} className="text-center" variant="bordered">
                <div className="mb-4">
                  <div className="text-4xl font-bold text-gardenia-red mb-2">
                    {member.count}
                  </div>
                  <Card.Title className="text-xl">{member.role}</Card.Title>
                </div>
                <Card.Content>
                  {member.description}
                </Card.Content>
              </Card>
            ))}
          </div>

          {/* Stats */}
          <div className="bg-gradient-to-r from-gardenia-red/5 to-gardenia-green/5 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-gardenia-red mb-2">8+</div>
                <div className="text-gardenia-dark font-semibold">Años de Experiencia</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gardenia-green mb-2">100%</div>
                <div className="text-gardenia-dark font-semibold">Docentes Licenciados</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gardenia-red mb-2">95%</div>
                <div className="text-gardenia-dark font-semibold">Satisfacción Familiar</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gardenia-green mb-2">3</div>
                <div className="text-gardenia-dark font-semibold">Niveles Educativos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-gardenia-red to-gardenia-green text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Quieres conocer más sobre nosotros?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Te invitamos a visitar nuestras instalaciones y conocer de cerca 
            todo lo que Las Gardenias puede ofrecer a tu familia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/contacto" variant="gold" size="lg">
              Agenda una Visita
            </Button>
            <Button to="/programas" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gardenia-red">
              Ver Nuestros Programas
            </Button>
          </div>
        </div>
      </section>
    </div>    
  )
}

export default About